import { useState } from "react";

import { Markdown } from "@/components/Markdown";
import type { AskEstimate, AskReference } from "@/lib/ask";
import { GrammarCard } from "@/components/dictionary/GrammarCard";
import type { Definition } from "@/lib/search";

import { useStickToBottom } from "./askScroll";
import type { CardSignal } from "./cardSignal";
import { ResultCard } from "./ResultCard";
import {
  chipLabel,
  entriesOf,
  grammarEntriesOf,
  isPresentation,
  splitEntries,
  type AskRow,
} from "./askTranscript";

/**
 * Ask — the dock's second segment (日本-325, design frame `1b`).
 *
 * **The payoff is that answers are made of app parts.** When the agent looks a word up, the row it
 * produces is not prose about the entry: it is the real `ResultCard`, with the same badges, the
 * same in-collection state and the same `A` to add. A question becomes a card without leaving the
 * sentence.
 *
 * **Tool calls are one muted monospace line each** — `⌕ dict · 仙人掌 → 2件`. Enough to
 * audit, not a machine narrating itself. A collapsed line in the model's context is the same
 * string, which is what makes tool memory honest rather than a silent loss.
 *
 * **Answers are markdown.** A model explaining grammar reaches for a table and a list, and left as
 * plain text those arrive as pipe rubble with the asterisks showing. See `components/Markdown.tsx`,
 * which refuses raw HTML so there is no sanitisation surface here at all.
 *
 * **Shortcut hints name Windows keys.** This app runs on one machine, which is a Windows desktop;
 * `⌘`/`⌥` glyphs would name keys that are not on the keyboard in front of the reader. The handlers
 * still accept `metaKey`, because accepting a key nobody will press costs nothing and refusing one
 * somebody does is a bug.
 */
export function AskPanel({
  configured,
  model,
  scope,
  rows,
  busy,
  error,
  droppedTurns,
  chips,
  draft,
  ordinalOf,
  onDraft,
  onRemoveChip,
  onSend,
  onStop,
  onClear,
  onDeleteLast,
  signalFor,
  onAddEntry,
  onOpenGrammar,
  estimate,
  isPhone = false,
}: {
  configured: boolean;
  model: string;
  scope: string;
  rows: AskRow[];
  busy: boolean;
  error: string | null;
  droppedTurns: number;
  chips: AskReference[];
  draft: string;
  ordinalOf: (sentenceId: string) => number | undefined;
  onDraft: (value: string) => void;
  onRemoveChip: (sentenceId: string) => void;
  onSend: () => void;
  onStop: () => void;
  onClear: () => void;
  onDeleteLast: () => void;
  signalFor: (term: string) => CardSignal;
  onAddEntry: (entry: Definition) => void;
  /**
   * Open a DoJG entry in 文法 (日本-519) — what a grammar card's corner does everywhere else.
   *
   * Supplied by `Read` rather than done here, because it is a navigation. Optional so a caller with
   * nowhere to navigate simply draws the card without the corner acting.
   */
  onOpenGrammar?: (entryId: string) => void;
  estimate: AskEstimate | null;
  isPhone?: boolean;
}) {
  // Follow the answer as it streams, and open on the end of the thread rather than its beginning
  // (日本-488). The rule — and why the distance is read on the scroll event rather than after a
  // mutation — is in `askScroll.ts`; nothing here needs to know when the transcript changed.
  const stick = useStickToBottom();

  if (!configured) {
    return (
      <div className={isPhone ? "flex h-full flex-col gap-3" : "flex h-full flex-col gap-3 px-[26px] pt-[13px]"}>
        <p className="text-secondary text-ink-3">
          質問には <code className="font-mono">OPENROUTER_API_KEY</code> が必要です。
        </p>
      </div>
    );
  }

  return (
    <div
      className={
        isPhone
          ? "flex h-full min-h-0 flex-col gap-[10px]"
          : "flex h-full min-h-0 flex-col gap-[10px] px-[26px] pt-[13px]"
      }
    >
      <div className="flex shrink-0 items-center gap-[10px]">
        <span className="text-meta text-ink-4">{scope}</span>
        <span className="h-px flex-1 bg-line" />
        <button
          type="button"
          onClick={onClear}
          className="shrink-0 font-mono text-meta text-ink-4 hover:text-ink-2"
        >
          消去
        </button>
      </div>

      {/* Two boxes, and the split is load-bearing rather than tidiness: the outer one scrolls and
          the inner one is the single thing whose growth is measured. Collapsing them back into one
          — which is what this was, and reads as the obvious simplification — leaves the observer
          watching only the scroller, whose own height does not change when an answer streams into
          it, and the follow stops working with nothing on screen saying so. */}
      <div
        ref={stick.scrollRef}
        onScroll={stick.onScroll}
        className="min-h-0 flex-1 overflow-auto pb-3"
      >
        <div ref={stick.contentRef} className="flex flex-col gap-3">
          {rows.length === 0 && !busy && (
            <p className="text-secondary text-ink-3">
              このログについて質問できます。<span className="font-mono">Ctrl+/</span>
              で選択中の文を指定します。
            </p>
          )}

          {/* Never silent about what was given up. A transcript that quietly stopped sending its
              oldest turns would read as one the model still remembers in full. */}
          {droppedTurns > 0 && (
            <p className="font-mono text-meta text-ink-4">
              — 以前のやり取りは送信されていません（{droppedTurns}件省略）—
            </p>
          )}

          {rows.map((row, index) => (
            <div key={row.id} className="group flex flex-col gap-[6px]">
              <Row
                row={row}
                signalFor={signalFor}
                onAddEntry={onAddEntry}
                onOpenGrammar={onOpenGrammar}
                isPhone={isPhone}
                ordinalOf={ordinalOf}
              />
              <RowActions
                // Only the text, never a tool result. A `dict` row's substance is the cards it drew,
                // which are already addable with `A` — copying its one summary line would hand back
                // `⌕ dict · 仙人掌 → 2件`, which is a label rather than an answer.
                copy={row.kind === "tool" ? null : row.text}
                // **The last row only, and not while an answer is streaming.** Anything further back
                // is load-bearing for the rounds after it, and a thread with a hole in the middle is
                // one the next question would be built from.
                onDelete={index === rows.length - 1 && !busy ? onDeleteLast : null}
                alwaysVisible={isPhone}
              />
            </div>
          ))}

          {busy && <span className="text-meta text-ink-4">考え中…</span>}
          {error && <span className="text-meta text-[color:var(--ink-3)]">{error}</span>}
        </div>
      </div>

      <Composer
        chips={chips}
        draft={draft}
        busy={busy}
        model={model}
        estimate={estimate}
        ordinalOf={ordinalOf}
        onDraft={onDraft}
        onRemoveChip={onRemoveChip}
        // **Sending is an unambiguous intent to be at the bottom**, whatever the reader had
        // scrolled up to re-read: their own question is about to be appended, and an answer
        // arriving where they cannot see it reads as a send that failed. The one place a pin is
        // declared rather than inferred from a gesture.
        //
        // **Only when something is actually sent.** The composer's Enter branch calls `onSend`
        // unconditionally and `useAsk.send` then refuses an empty draft or a turn already
        // streaming — so an unguarded pin here undoes a deliberate mid-stream scroll-up for a
        // keystroke that sent nothing, which is the one property the un-pin check protects.
        onSend={() => {
          if (draft.trim() && !busy) stick.pin();
          onSend();
        }}
        onStop={onStop}
        isPhone={isPhone}
      />
    </div>
  );
}

function Row({
  row,
  signalFor,
  onAddEntry,
  onOpenGrammar,
  isPhone,
  ordinalOf,
}: {
  row: AskRow;
  signalFor: (term: string) => CardSignal;
  onAddEntry: (entry: Definition) => void;
  onOpenGrammar?: (entryId: string) => void;
  isPhone: boolean;
  ordinalOf: (sentenceId: string) => number | undefined;
}) {
  if (row.kind === "question") {
    return (
      <div className="flex flex-col gap-[6px]">
        {row.references.length > 0 && (
          <div className="flex flex-wrap gap-[6px]">
            {row.references.map((reference) => (
              <span
                key={reference.sentenceId}
                className="rounded-[var(--r-2)] border border-accent-border bg-accent-tint px-[7px] py-[3px] font-jp text-[14px]/[1.3] text-accent-text"
              >
                {chipLabel(reference, ordinalOf(reference.sentenceId))}
              </span>
            ))}
          </div>
        )}
        <div className="text-secondary font-semibold text-ink">
          <Markdown>{row.text}</Markdown>
        </div>
      </div>
    );
  }

  if (row.kind === "answer") {
    return (
      <div className="flex flex-col gap-[6px]">
        {row.reasoning.trim() && <Reasoning text={row.reasoning} />}
        {row.text.trim() && (
          <div className="text-secondary text-ink-2">
            <Markdown>{row.text}</Markdown>
          </div>
        )}
      </div>
    );
  }

  return (
    <ToolRow
      row={row}
      signalFor={signalFor}
      onAddEntry={onAddEntry}
      onOpenGrammar={onOpenGrammar}
      isPhone={isPhone}
    />
  );
}

/**
 * Copy, and — on the last row only — delete.
 *
 * **Revealed on hover rather than drawn always.** A control strip under every message in a 700px
 * column doubles the vertical cost of a transcript that is already the thing being read. `hover`
 * alone would hide it from the keyboard, so `focus-within` opens it too, and a phone (no hover at
 * all) gets it permanently.
 *
 * **Copy takes the markdown source, not the rendered text.** What the model wrote is a table or a
 * list, and pasting it somewhere that renders markdown should reproduce it; pasting it somewhere
 * that does not should still show the structure rather than a run-together paragraph.
 */
function RowActions({
  copy,
  onDelete,
  alwaysVisible,
}: {
  copy: string | null;
  onDelete: (() => void) | null;
  alwaysVisible: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const hasCopy = Boolean(copy && copy.trim());
  if (!hasCopy && !onDelete) return null;

  const write = () => {
    // `navigator.clipboard` is undefined on an insecure origin, which `?.` covers; a denied
    // permission rejects, which the catch covers. Neither is worth an error line under a message —
    // the text is on screen to select by hand either way.
    void navigator.clipboard
      ?.writeText(copy ?? "")
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };

  return (
    <div
      className={`flex items-center gap-[10px] ${
        alwaysVisible
          ? ""
          : "opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100"
      }`}
    >
      {hasCopy && (
        <button
          type="button"
          onClick={write}
          aria-label={copied ? "コピーしました" : "このメッセージをコピー"}
          className="font-mono text-meta text-ink-4 hover:text-ink-2"
        >
          {copied ? "コピー済" : "コピー"}
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          title="このメッセージを削除します。AIの返答はツールの結果ごと消え、もう一度押すと質問も取り消せます。"
          aria-label="最後のメッセージを削除"
          className="font-mono text-meta text-ink-4 hover:text-[color:var(--ink-3)]"
        >
          削除
        </button>
      )}
    </div>
  );
}

/**
 * The model's thinking, collapsed.
 *
 * **Above the answer and shut by default.** It explains the reply rather than being part of it, and
 * it is routinely longer than the reply — so open by default it would bury the thing the reader
 * asked for under the model's working. Kept as plain text, not markdown: a reasoning trace is
 * stream-of-thought and half-written markdown mid-stream renders as garbage that then reflows.
 */
function Reasoning({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-fit items-center gap-[6px] font-mono text-meta text-ink-4 hover:text-ink-2"
      >
        <span className="font-jp uppercase tracking-[.12em]">考え中</span>
        <span className={open ? "rotate-90 transition-transform" : "transition-transform"}>›</span>
        <span>{open ? "非表示" : "表示"}</span>
      </button>
      {open && (
        <div className="max-h-[280px] overflow-auto whitespace-pre-wrap rounded-[var(--r-2)] border border-line bg-sunken p-2 text-[11.5px]/[1.5] text-ink-3">
          {text}
        </div>
      )}
    </div>
  );
}

/**
 * One tool call, its summary line, and any cards it produced.
 *
 * **Two shapes, decided by which tool ran.** A `dict` lookup is the model's own reference — it is
 * called freely, including for words the model then discards — so it draws as **one collapsed
 * line**, openable if the reader wants to check the working. `present_definitions` is the model
 * choosing what belongs in the explanation, so those cards are **drawn in full, with no fold**:
 * folding them would hide the part of the answer the model deliberately put there, and the server
 * has already capped how many can arrive.
 *
 * Inside an opened lookup the old fold still applies — a bare kana string resolves to dozens of
 * entries, and the fold names the count it is holding back rather than just offering "more".
 */
function ToolRow({
  row,
  signalFor,
  onAddEntry,
  onOpenGrammar,
  isPhone,
}: {
  row: Extract<AskRow, { kind: "tool" }>;
  signalFor: (term: string) => CardSignal;
  onAddEntry: (entry: Definition) => void;
  onOpenGrammar?: (entryId: string) => void;
  isPhone: boolean;
}) {
  const presented = isPresentation(row);
  const [open, setOpen] = useState(presented);
  const [expanded, setExpanded] = useState(false);
  // Which grammar entry is unfolded inside this row. Local, and by id rather than by index, for
  // `ResultList`'s reason: the list it indexes is the payload's and nothing else addresses it.
  const [openGrammar, setOpenGrammar] = useState<string | null>(null);
  /**
   * **The two payload shapes, told apart by the tool that produced them** (日本-519).
   *
   * `dict` / `present_definitions` carry `Definition`s and draw as `ResultCard`s; `grammar` /
   * `present_grammar` carry `GrammarEntry`s and draw as `GrammarCard`s. Handing a grammar entry to
   * `ResultCard` is not a cosmetic mismatch — it has no `senses`, so the card throws on
   * `entry.senses.map` and takes the whole transcript with it.
   */
  const all = entriesOf(row);
  const grammar = grammarEntriesOf(row);
  const cards = grammar?.length ?? all?.length ?? 0;
  const { shown, hidden } = presented
    ? { shown: [...(all ?? [])], hidden: 0 }
    : splitEntries(open ? all : undefined, expanded);
  // A grammar card is large and there are at most `MAX_PRESENTED_GRAMMAR` of them, so there is no
  // fold here: presented entries draw, a `grammar` lookup's draw once its line is opened.
  const grammarShown = presented || open ? (grammar ?? []) : [];

  const line = row.pending ? `⌕ ${row.name}…` : row.summary;
  const tone = row.ok ? "text-ink-4" : "text-[color:var(--ink-3)]";

  return (
    <div className="flex flex-col gap-2">
      {/* A lookup with cards behind it is the toggle; anything else is just a line. A `collection`
          answer or a `set_translation` receipt has nothing to open, and a disclosure caret on one
          would promise something that is not there. */}
      {!presented && cards > 0 ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={`flex w-fit items-center gap-[6px] font-mono text-meta hover:text-ink-2 ${tone}`}
        >
          <span className={open ? "rotate-90 transition-transform" : "transition-transform"}>›</span>
          {line}
        </button>
      ) : (
        <span className={`font-mono text-meta ${tone}`}>{line}</span>
      )}

      {grammarShown.map((entry) => (
        <GrammarCard
          key={`${row.id}-${entry.id}`}
          entry={entry}
          focused={false}
          expanded={openGrammar === entry.id}
          isPhone={isPhone}
          onFocus={() => undefined}
          onToggle={() => setOpenGrammar((at) => (at === entry.id ? null : entry.id))}
          onOpen={() => onOpenGrammar?.(entry.id)}
        />
      ))}

      {shown.map((entry, index) => (
        <ResultCard
          // Indexed for `Read.tsx`'s reason: one term legitimately spans several rows, so a
          // term-only key collides.
          key={`${row.id}-${entry.term}-${index}`}
          entry={entry}
          signal={signalFor(entry.term)}
          focused={false}
          collapsed={false}
          isPhone={isPhone}
          onFocus={() => undefined}
          onAdd={() => onAddEntry(entry)}
          onEdit={() => undefined}
        />
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-fit font-mono text-meta text-ink-4 hover:text-ink-2"
        >
          他{hidden}件を表示 ›
        </button>
      )}
      {open && expanded && shown.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="w-fit font-mono text-meta text-ink-4 hover:text-ink-2"
        >
          ‹ 表示を減らす
        </button>
      )}
    </div>
  );
}

const SUGGESTIONS = ["訳して", "文法を分解して", "誰が話している？"];

/**
 * Put a newline at the caret and hand the result back to the controlled value.
 *
 * The textarea's value belongs to React, so `setRangeText` alone would be overwritten on the next
 * render. The caret is restored after the state lands, or it jumps to the end of the box on every
 * Ctrl+Enter — which is worse than not inserting the newline at all.
 */
function insertNewline(el: HTMLTextAreaElement, onDraft: (value: string) => void) {
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  onDraft(`${el.value.slice(0, start)}\n${el.value.slice(end)}`);
  requestAnimationFrame(() => {
    el.selectionStart = el.selectionEnd = start + 1;
  });
}

/**
 * What the next request would cost, beside the button that sends it.
 *
 * **The one thing this app has that its sibling does not**, and the reason it is worth the round
 * trip: Ask re-sends a growing transcript every turn, so the bill climbs with the conversation and
 * nothing else on screen would ever say so. A reader who can see 12k tokens going out knows to
 * Clear; one who cannot, does not.
 *
 * Three honesty rules, all visible in the markup:
 *
 * - **`~` on everything.** The input side is a `tiktoken` estimate of the real assembled list, not
 *   the model's own count, which OpenRouter reports only after the fact.
 * - **The output half is marked as a guess** (`+出力~350`) and its tooltip says what it rests on.
 *   With no history it is a constant, and dressing a constant as a measurement is the failure this
 *   whole feature exists to avoid.
 * - **No price at all when the catalogue quotes none.** Unknown and free must never look the same.
 */
function Cost({ estimate }: { estimate: AskEstimate | null }) {
  if (!estimate) return null;
  const { promptTokens, completionTokens, outputSamples, cost } = estimate;
  const basis =
    outputSamples > 0
      ? `出力はこのモデルの直近${outputSamples}件の回答からの推定です`
      : "出力は既定値です。このモデルの回答実績がまだありません";
  return (
    <span
      className="font-mono text-meta text-ink-4"
      title={`次のリクエストの入力は約${promptTokens.toLocaleString()}トークン。${basis}。ツールを呼ぶ回答は複数リクエストになります。`}
    >
      入力~{formatTokens(promptTokens)} +出力~{formatTokens(completionTokens)}
      {cost !== null && ` · ~${formatCost(cost)}`}
    </span>
  );
}

function formatTokens(value: number): string {
  return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(value);
}

/** Sub-cent costs are the normal case here, so two decimal places would draw `$0.00` on every
 *  request and make the whole figure look broken.
 *
 *  **The unit is always dollars, and it is the precision that moves.** This used to switch to a
 *  `¢` figure below a cent, which is the obvious fix and is misread: `0.19¢` is nineteen
 *  hundredths of a cent, and it was taken for nineteen cents — a hundredfold error, in the one
 *  number on screen whose entire job is to be trusted at a glance. A reader who mistrusts a model
 *  because of a misplaced decimal point is worse off than one who sees no figure at all. Note the
 *  `$0.00` branch is unreachable on a cheap model — DeepSeek v4 Pro needs a ~45k-token prompt to
 *  cross a cent — so the four-decimal form is what is actually drawn.
 *
 *  The floor matters for the same reason: rounding a real cost to `$0.0000` makes a request that
 *  costs something look free, which is the failure `Cost`'s own rule names. */
function formatCost(value: number): string {
  if (value >= 0.01) return `$${value.toFixed(2)}`;
  if (value === 0) return "$0";
  if (value < 0.0001) return "<$0.0001";
  return `$${value.toFixed(4)}`;
}

function Composer({
  chips,
  draft,
  busy,
  model,
  estimate,
  ordinalOf,
  onDraft,
  onRemoveChip,
  onSend,
  onStop,
  isPhone,
}: {
  chips: AskReference[];
  draft: string;
  busy: boolean;
  model: string;
  estimate: AskEstimate | null;
  ordinalOf: (sentenceId: string) => number | undefined;
  onDraft: (value: string) => void;
  onRemoveChip: (sentenceId: string) => void;
  onSend: () => void;
  onStop: () => void;
  isPhone: boolean;
}) {
  return (
    <div className="shrink-0 border-t border-line-soft pt-[10px] pb-[13px]">
      {chips.length > 0 && (
        <div className="mb-[6px] flex flex-wrap gap-[6px]">
          {chips.map((chip) => (
            <button
              key={chip.sentenceId}
              type="button"
              onClick={() => onRemoveChip(chip.sentenceId)}
              className="rounded-[var(--r-2)] border border-accent-border bg-accent-tint px-[7px] py-[3px] font-jp text-[14px]/[1.3] text-accent-text"
              aria-label={`${chip.text}を外す`}
            >
              {chipLabel(chip, ordinalOf(chip.sentenceId))} ×
            </button>
          ))}
        </div>
      )}

      {draft.trim() === "" && (
        <div className="mb-[6px] flex flex-wrap gap-[6px]">
          {SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => onDraft(suggestion)}
              className="rounded-[var(--r-2)] border border-line bg-void px-[7px] py-[3px] text-[14px]/[1.3] text-ink-3"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      <textarea
        value={draft}
        onChange={(e) => onDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // **The IME guard, and in this app it is the important half.** A Japanese IME uses
            // Enter to commit a conversion, and that keystroke reaches this handler like any
            // other — so without this, typing 仙人掌 and pressing Enter to accept the kanji would
            // send the half-finished question instead. `isComposing` is the modern signal;
            // keyCode 229 is what older WebViews report while a composition is active.
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.shiftKey || e.ctrlKey || e.metaKey) {
              // Shift+Enter inserts a newline by itself; Ctrl+Enter does not — the browser gives
              // it no default action in a textarea — so it is inserted by hand, at the caret,
              // rather than being quietly swallowed.
              if (!e.shiftKey) {
                e.preventDefault();
                insertNewline(e.currentTarget, onDraft);
              }
            } else {
              e.preventDefault();
              onSend();
            }
          }
          // The dock's own shortcuts (`j`/`k`, `A`, `E`) must not fire while a question is being
          // typed — every one of them is a letter.
          e.stopPropagation();
        }}
        rows={2}
        placeholder="このログについて質問…"
        aria-label="質問を入力"
        className="w-full resize-none rounded-[var(--r-2)] border border-line-strong bg-void px-[10px] py-[7px] text-secondary text-ink"
      />

      <div className="mt-[6px] flex flex-wrap items-center gap-x-[10px] gap-y-1">
        <span className="font-mono text-meta text-ink-4">{model}</span>
        <Cost estimate={estimate} />
        <span className="flex-1" />
        {!isPhone && (
          <span className="font-mono text-meta text-ink-4">
            Enterで送信 · Shift+Enterで改行 · Ctrl+/でこの文を指定
          </span>
        )}
        {busy ? (
          // **Stop replaces Send rather than sitting beside it.** There is exactly one thing to do
          // with a composer while an answer is streaming, and two buttons where one applies is how
          // you end up sending a second question into a turn that has not finished.
          <button
            type="button"
            onClick={onStop}
            title="生成を停止"
            className="flex shrink-0 items-center gap-[6px] rounded-[var(--r-2)] border border-line-strong bg-void px-3 py-[6px] text-[14px]/[1] font-semibold text-ink-2 hover:text-ink"
          >
            <span
              aria-hidden
              className="block h-[9px] w-[9px] rounded-[var(--r-2)] bg-[color:var(--ink-3)]"
            />
            停止
          </button>
        ) : (
          <button
            type="button"
            onClick={onSend}
            disabled={!draft.trim()}
            className="shrink-0 rounded-[var(--r-2)] border border-transparent bg-ink px-3 py-[6px] text-[14px]/[1] font-semibold text-void disabled:opacity-40"
          >
            送信
          </button>
        )}
      </div>
    </div>
  );
}
